import { Col, Form, Input, InputNumber, Row, Typography } from "antd";
import { FormInstance } from "antd/es/form/Form";
import { useTranslation } from "react-i18next";
import { FaInfoCircle } from "react-icons/fa";
import useCoreStore from "views/hooks/useCoreStore";

interface FormatsFormPropsType {
  deliveryExample: string;
  invoiceExample: string;
  ordersExample: string;
  budgetExample: string;
  returnExample: string;
  clientExample: string;
  receiptExample: string;
  paymentInAdvanceExample: string;
  decimalPlacesForQuantity: string;
  suggestedPaElectronicAddress?: string;
  data: GetCompanySettingResponseData | undefined;
  form: FormInstance;
}

export default function FormatsForm(props: FormatsFormPropsType): React.ReactElement {
  const {
    data,
    ordersExample,
    budgetExample,
    returnExample,
    clientExample,
    receiptExample,
    invoiceExample,
    deliveryExample,
    decimalPlacesForQuantity,
    paymentInAdvanceExample,
    suggestedPaElectronicAddress,
  } = props;

  const companySettings = useCoreStore(state => state.companySettings);

  const { t } = useTranslation();

  return (
    <Row gutter={[16, 16]} className="numeration-format">
      <Col xs={24} sm={12}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="budgetFormat"
              label={t("Budget format")}
              rules={[{ required: true, message: t("This field is required") }]}
              extra={
                <span className="input-example">
                  {t("Example")}: {budgetExample}
                </span>
              }
              tooltip={{
                title: t("Format for budget numbering"),
                icon: <FaInfoCircle />,
              }}
            >
              <Input
                onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                  (e.target.value = e.target.value.toUpperCase())
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="budgetFirstNumber" label={t("Budget number")}>
              <InputNumber disabled={data?.canChangeBudgetNextNumber === false} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="deliveryFormFormat"
              label={t("Delivery format")}
              rules={[{ required: true, message: t("This field is required") }]}
              extra={
                <span className="input-example">
                  {t("Example")}: {deliveryExample}
                </span>
              }
              tooltip={{
                title: t("Format for delivery numbering"),
                icon: <FaInfoCircle />,
              }}
            >
              <Input
                onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                  (e.target.value = e.target.value.toUpperCase())
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="deliveryFormFirstNumber" label={t("Delivery number")}>
              <InputNumber disabled={data?.canChangeDeliveryFormNextNumber === false} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="saleOrderFormat"
              label={t("Orders format")}
              rules={[{ required: true, message: t("This field is required") }]}
              extra={
                <span className="input-example">
                  {t("Example")}: {ordersExample}
                </span>
              }
              tooltip={{
                title: t("Format for orders numbering"),
                icon: <FaInfoCircle />,
              }}
            >
              <Input
                onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                  (e.target.value = e.target.value.toUpperCase())
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="saleOrderFirstNumber" label={t("Orders number")}>
              <InputNumber disabled={data?.canChangeSaleOrderNextNumber === false} />
            </Form.Item>
          </Col>
        </Row>
      </Col>
      <Col xs={24} sm={12}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="purchaseOrderFormat"
              label={t("Invoices format")}
              rules={[{ required: true, message: t("This field is required") }]}
              extra={
                <span className="input-example">
                  {t("Example")}: {invoiceExample}
                </span>
              }
              tooltip={{
                title: t("Format for invoices numbering"),
                icon: <FaInfoCircle />,
              }}
            >
              <Input
                onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                  (e.target.value = e.target.value.toUpperCase())
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="purchaseOrderFirstNumber" label={t("Invoices number")}>
              <InputNumber disabled={data?.canChangePurchaseOrderNextNumber === false} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="returnFormat"
              label={t("Assets format")}
              rules={[{ required: true, message: t("This field is required") }]}
              extra={
                <span className="input-example">
                  {t("Example")}: {returnExample}
                </span>
              }
              tooltip={{
                title: t("Format for return numbering"),
                icon: <FaInfoCircle />,
              }}
            >
              <Input
                onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                  (e.target.value = e.target.value.toUpperCase())
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="returnFirstNumber" label={t("Return number")}>
              <InputNumber disabled={data?.canChangeReturnNextNumber === false} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="receiptFormat"
              label={t("Receipt format")}
              rules={[{ required: true, message: t("This field is required") }]}
              extra={
                <span className="input-example">
                  {t("Example")}: {receiptExample}
                </span>
              }
              tooltip={{
                title: t("Format for receipt numbering"),
                icon: <FaInfoCircle />,
              }}
            >
              <Input
                onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                  (e.target.value = e.target.value.toUpperCase())
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="receiptFirstNumber" label={t("Receipt number")}>
              <InputNumber disabled={data?.canChangeReceiptNextNumber === false} />
            </Form.Item>
          </Col>
        </Row>
      </Col>
      <Col xs={24} sm={24}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="paymentInAdvanceFormat"
              label={t("Payment in advance format")}
              rules={[{ required: true, message: t("This field is required") }]}
              extra={
                <span className="input-example">
                  {t("Example")}: {paymentInAdvanceExample}
                </span>
              }
              tooltip={{
                title: t("Format for payment in advance numbering"),
                icon: <FaInfoCircle />,
              }}
            >
              <Input
                onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                  (e.target.value = e.target.value.toUpperCase())
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="paymentInAdvanceFirstNumber" label={t("Payment in advance number")}>
              <InputNumber disabled={data?.canChangePaymentInAdvanceNextNumber === false} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="clientFormat"
              label={t("Client format")}
              rules={[{ required: true, message: t("This field is required") }]}
              extra={
                <span className="input-example">
                  {t("Example")}: {clientExample}
                </span>
              }
              tooltip={{
                title: t("Format for client numbering"),
                icon: <FaInfoCircle />,
              }}
            >
              <Input
                disabled={!companySettings?.clientHasAutoNumber}
                onInput={(e: React.ChangeEvent<HTMLInputElement>) =>
                  (e.target.value = e.target.value.toUpperCase())
                }
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="decimalPlacesForQuantity"
              label={t("Decimal places for quantity")}
              extra={
                <>
                  <span className="input-example">
                    {t("Example")}: {decimalPlacesForQuantity}
                  </span>
                  {" | "}
                  <span className="input-example"> {t("Max")}: 5</span>
                </>
              }
              tooltip={{
                title: t("Maximum number of decimal places to display for quantities"),
                icon: <FaInfoCircle />,
              }}
            >
              <InputNumber maxLength={1} max={5} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="paElectronicAddress"
              label={t("Electronic address PA")}
              extra={
                suggestedPaElectronicAddress ? (
                  <span className="input-example">
                    {t("Suggested (SIREN)")}: {suggestedPaElectronicAddress}
                  </span>
                ) : undefined
              }
              tooltip={{
                title: t("Electronic address used to send invoices to the public platform"),
                icon: <FaInfoCircle />,
              }}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Col>
      <Col span={24}>
        <div className="numeration-format-info">
          <div className="numeration-format-info__header">
            <FaInfoCircle />
            <span>{t("How to define your numbering formats?")}</span>
          </div>
          <div className="numeration-format-info__body">
            <div className="numeration-format-info__codes">
              <code>AA / AAAA</code>
              <span>{t("Year")}</span>
            </div>
            <div className="numeration-format-info__codes">
              <code>MM</code>
              <span>{t("Month")}</span>
            </div>
            <div className="numeration-format-info__codes">
              <code>X</code>
              <span>{t("Sequence")}</span>
            </div>
          </div>
          <div className="numeration-format-info__example">
            <span className="numeration-format-info__example-label">{t("Example")}</span>
            <code>FACT-AAMM-XXXX</code>
            <span className="numeration-format-info__example-arrow">→</span>
            <code>FACT-2301-0014</code>
          </div>
          <Typography.Text className="numeration-format-info__note" type="secondary">
            {t(
              "The invoice number must be unique, based on a chronological and continuous sequence.",
            )}{" "}
            {t("Avoid numbering without separators such as")} <code>AAMMXXXX</code>.
          </Typography.Text>
        </div>
      </Col>
    </Row>
  );
}
